import axios from "axios";
import { Cookies } from "../constants/Cookies";
import { util } from "../config/util";
import GlobalEnvironments from "../modules/globalEnvironment/GlobalEnvironment";
import { logEvent } from "../services/eventLog";

/**
 * 처음 들어온 경로(유입) 기록 — 2단계 가입 직후 착지(10-08) + 10-28 핫픽스(광고 키워드 → 가입 → 결제 연결).
 * 1) 앱이 뜰 때(UTM 을 URL 에서 지우기 전) 이 브라우저의 "첫 방문" 경로를 localStorage 에 한 번만 저장한다.
 * 2) 로그인된 상태가 되면 서버 event_log 에 first_touch 로 한 번 보낸다(서버도 유저당 1건만 저장).
 * 유튜브는 영상 주소를, 네이버 자연 검색은 검색어를 넘겨주지 않으므로 영상·캠페인 구분은 링크의 utm_* 꼬리표로 한다.
 * 네이버 검색광고는 자동 추적이 붙이는 n_keyword·n_query 등(n_*)으로 키워드까지 남긴다.
 */
const STORE_KEY = "conax_first_touch";
const SENT_KEY = "conax_first_touch_sent";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
// 네이버 검색광고 자동 추적 파라미터 (서버 event_log FIRST_TOUCH_META_KEYS 와 같은 목록)
export const NAVER_AD_KEYS = [
  "n_media", "n_query", "n_rank", "n_ad_group", "n_ad", "n_keyword_id", "n_keyword",
  "n_campaign_type", "n_ad_group_type", "n_match",
];

export interface FirstTouch {
  channel: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer_host?: string;
  landing?: string;
  first_seen?: string;
  [naverKey: string]: string | undefined;
}

// 우리 사이트 도메인(내부 이동은 유입이 아님)
const OWN_HOST = /(^|\.)(ddusul\.com|conax\.co\.kr|ddukeong\.com|localhost|127\.0\.0\.1)$/;

/** 채널 이름: utm_source 가 있으면 그것, 없으면 이전 페이지 주소로 분류. */
export const classifyChannel = (utmSource: string | null | undefined, referrerHost: string | null | undefined): string => {
  const utm = (utmSource || "").trim().toLowerCase();
  if (utm) return utm.slice(0, 40);
  const host = (referrerHost || "").toLowerCase();
  if (!host || OWN_HOST.test(host)) return "direct";
  if (/(^|\.)(youtube\.com|youtu\.be)$/.test(host)) return "youtube";
  if (/blog\.naver\.com$/.test(host)) return "naver_blog";
  if (/cafe\.naver\.com$/.test(host)) return "naver_cafe";
  if (/ad\.search\.naver\.com$/.test(host)) return "naver_ad";
  if (/(^|\.)naver\.com$/.test(host)) return "naver";
  if (/gemini\.google\.com$/.test(host)) return "gemini";
  if (/(^|\.)google\./.test(host)) return "google";
  if (/(^|\.)daum\.net$/.test(host)) return "daum";
  if (/(^|\.)bing\.com$/.test(host)) return "bing";
  if (/chatgpt\.com$|openai\.com$/.test(host)) return "chatgpt";
  if (/(^|\.)(facebook\.com|instagram\.com|kakao\.com|band\.us)$/.test(host)) return "sns";
  return "other:" + host.slice(0, 40);
};

const safeGet = (k: string) => {
  try {
    return window.localStorage.getItem(k);
  } catch (e) {
    return null;
  }
};
const safeSet = (k: string, v: string) => {
  try {
    window.localStorage.setItem(k, v);
  } catch (e) {
    // 비공개 창 등에서 저장이 막혀도 무시
  }
};

/** 앱 시작 시 1회 호출. 이미 저장돼 있으면 아무것도 하지 않는다(첫 방문 경로 유지). */
export const captureFirstTouch = () => {
  try {
    if (safeGet(STORE_KEY)) return;
    const url = new URL(window.location.href);
    let referrerHost = "";
    try {
      referrerHost = document.referrer ? new URL(document.referrer).hostname : "";
    } catch (e) {
      referrerHost = "";
    }
    const ft: FirstTouch = { channel: "" };
    UTM_KEYS.concat(NAVER_AD_KEYS).forEach((k) => {
      const v = url.searchParams.get(k);
      if (v) (ft as any)[k] = v.slice(0, 200);
    });
    if (referrerHost && !OWN_HOST.test(referrerHost)) ft.referrer_host = referrerHost;
    ft.landing = url.pathname.slice(0, 200);
    ft.first_seen = new Date().toISOString();
    // 꼬리표 없이 자동 추적 값만 있으면 네이버 검색광고로 본다
    ft.channel = classifyChannel(ft.utm_source || (ft.n_keyword || ft.n_media ? "naver_ad" : ""), ft.referrer_host);
    safeSet(STORE_KEY, JSON.stringify(ft));
  } catch (e) {
    // 기록 실패는 무시
  }
};

export const getFirstTouch = (): FirstTouch | null => {
  try {
    const raw = safeGet(STORE_KEY);
    return raw ? (JSON.parse(raw) as FirstTouch) : null;
  } catch (e) {
    return null;
  }
};

/** 로그인된 상태에서 1회 서버로 보낸다(이 브라우저에서 이미 보냈으면 생략). */
export const sendFirstTouchOnce = () => {
  if (!util.getCookie(Cookies.authKey) || safeGet(SENT_KEY)) return;
  const ft = getFirstTouch();
  if (!ft) return;
  const { channel, ...meta } = ft;
  logEvent("first_touch", channel || "unknown", meta);
  safeSet(SENT_KEY, "1");
};

/** 가입 7일 안이고 시작 화면에서 아직 고르지 않았으면 true. 오류면 false. */
export const fetchShowOnboarding = async (): Promise<boolean> => {
  try {
    const key = util.getCookie(Cookies.authKey);
    if (!key) return false;
    const res = await axios.get(`${GlobalEnvironments.getApiUrl()}event_log/onboarding/`, {
      headers: { Authorization: `Token ${key}` },
      timeout: 4000,
    });
    return !!res?.data?.show;
  } catch (e) {
    return false;
  }
};

/** 로그인·가입 직후 이동: 시작 화면을 보여 줄 사람은 /welcome, 아니면 첫 화면. */
export const goAfterLogin = async (history: { replace: (p: string) => void }, fallback: string, welcome: string) => {
  sendFirstTouchOnce();
  const show = await fetchShowOnboarding();
  history.replace(show ? welcome : fallback);
};
